import type { AdminWithdrawalDto, WithdrawalStatus } from '@bravo/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/DataTable';
import {
  Badge,
  Button,
  Empty,
  ErrorBlock,
  Field,
  PageHeader,
  Spinner,
  TextArea,
  TextInput,
  statusTone,
} from '../components/ui';
import { apiData, apiPage } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { useFormat } from '../lib/format';

const STATUSES: WithdrawalStatus[] = ['pending', 'processing', 'completed', 'rejected'];

async function copyText(value: string, ok: string) {
  await navigator.clipboard.writeText(value);
  toastOk(ok);
}

export function WithdrawalsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<WithdrawalStatus>('pending');
  const [selected, setSelected] = useState<AdminWithdrawalDto | null>(null);
  const [transferRef, setTransferRef] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ['withdrawals', status],
    queryFn: ({ pageParam }) =>
      apiPage<AdminWithdrawalDto>('/admin/withdrawals', { status, limit: 20, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  async function act(path: string, body?: unknown, message?: string) {
    setBusy(true);
    try {
      await apiData(path, { method: 'POST', body: body ?? {} });
      toastOk(message ?? t('saved'));
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  const columns: ColumnDef<AdminWithdrawalDto>[] = [
    {
      id: 'user',
      header: t('user'),
      cell: ({ row }) => row.original.userName || row.original.userPhone || row.original.userId,
    },
    { id: 'method', header: t('method'), cell: ({ row }) => format.method(row.original.method) },
    { id: 'amount', header: t('amount'), cell: ({ row }) => format.etb(row.original.amount) },
    { id: 'account', header: t('withdrawals.account'), cell: ({ row }) => row.original.accountNumber },
    { id: 'time', header: t('time'), cell: ({ row }) => format.when(row.original.createdAt) },
    {
      id: 'status',
      header: t('status'),
      cell: ({ row }) => (
        <Badge tone={statusTone(row.original.status)}>{format.status(row.original.status)}</Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={t('withdrawals.title')} subtitle={t('withdrawals.subtitle')} />
      <div className="mb-4 flex flex-wrap gap-2">
        {STATUSES.map((item) => (
          <Button key={item} variant={item === status ? 'primary' : 'secondary'} onClick={() => setStatus(item)}>
            {format.status(item)}
          </Button>
        ))}
      </div>
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} /> : null}
      {!query.isLoading && items.length === 0 ? <Empty title={t('withdrawals.empty')} /> : null}
      {items.length > 0 ? <DataTable columns={columns} data={items} onRow={setSelected} /> : null}
      {query.hasNextPage ? (
        <Button className="mt-4" variant="secondary" onClick={() => void query.fetchNextPage()}>
          {t('loadMore')}
        </Button>
      ) : null}

      {selected ? (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="text-lg font-semibold">{format.etb(selected.amount)}</h2>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-fg-muted">{t('user')}</dt>
              <dd>
                <Link className="text-brand-text underline" to={`/users/${selected.userId}`}>
                  {selected.userName || selected.userPhone || selected.userId}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('withdrawals.kyc')}</dt>
              <dd>
                {selected.kycFullName || t('withdrawals.noKyc')} · {selected.kycIdNumber || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('withdrawals.accountName')}</dt>
              <dd>{selected.accountName}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('withdrawals.account')}</dt>
              <dd className="flex items-center gap-2">
                <span className="font-mono">{selected.accountNumber}</span>
                <Button
                  variant="ghost"
                  onClick={() => void copyText(selected.accountNumber, t('copied'))}
                >
                  {t('copy')}
                </Button>
              </dd>
            </div>
          </dl>
          {selected.status === 'pending' ? (
            <Button
              className="mt-4"
              data-testid="withdrawal-start"
              loading={busy}
              onClick={() => void act(`/admin/withdrawals/${selected.id}/start`, {}, t('withdrawals.started'))}
            >
              {t('withdrawals.start')}
            </Button>
          ) : null}
          {selected.status === 'processing' ? (
            <div className="mt-4 space-y-3">
              <Field label={t('withdrawals.transferRef')}>
                <TextInput
                  data-testid="withdrawal-ref"
                  value={transferRef}
                  onChange={(event) => setTransferRef(event.target.value)}
                />
              </Field>
              <Button
                data-testid="withdrawal-complete"
                loading={busy}
                disabled={transferRef.trim().length < 3}
                onClick={() =>
                  void act(
                    `/admin/withdrawals/${selected.id}/complete`,
                    { adminTransferRef: transferRef.trim() },
                    t('withdrawals.completed'),
                  )
                }
              >
                {t('withdrawals.complete')}
              </Button>
            </div>
          ) : null}
          {selected.status === 'pending' || selected.status === 'processing' ? (
            <div className="mt-4 space-y-3">
              <Field label={t('reason')}>
                <TextArea value={reason} onChange={(event) => setReason(event.target.value)} />
              </Field>
              <Button
                variant="danger"
                loading={busy}
                disabled={reason.trim().length < 3}
                onClick={() =>
                  void act(
                    `/admin/withdrawals/${selected.id}/reject`,
                    { reason: reason.trim() },
                    t('withdrawals.rejected'),
                  )
                }
              >
                {t('withdrawals.reject')}
              </Button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
